import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  selection,
} from 'redux-resource-plugins';
import {
  getStatus,
} from 'redux-resource';
import {
  actionTypes,
  addResources,
  deleteResources,
  setProfileState,
  requestInviteClient,
} from '../actions';
import {
  getProfile,
  getSelectedIds,
  getResourceById,
} from '../selectors';
import {
  throwError,
  getEntityType,
} from './helpers';
import {
  capitalizeFirstLetter,
} from '../utils';
import api from '../api';

export default function* clientsFlow(reportChannel) {
  function* fetchClients({
    qs,
    list,
    requestKey,
    resourceType,
    mergeListIds = false,
  }) {
    const actions = createActionCreators('read', {
      resourceType: resourceType || 'clients',
      requestKey: requestKey || 'fetchClients',
    });

    try {
      yield eff.put(actions.pending());

      const results = yield eff.call(
        api.getClients,
        {
          qs,
        },
      );

      const meta = results[0];

      const resources = Array.isArray(results[1]) ? results[1] : results;

      yield eff.put(actions.succeeded({
        list,
        resources,
        mergeListIds,
        requestProperties: {
          meta,
        },
      }));
    } catch (err) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, err);
    }
  }

  function* fetchClient({ clientId }) {
    const actions = createActionCreators('read', {
      resourceType: 'clients',
      requestKey: 'fetchClient',
    });

    yield eff.put(actions.pending());

    try {
      const client = yield eff.call(
        api.getEntity,
        {
          id: clientId,
          // gymcloud requests to /users/:id, not /clients/:id
          type: 'users',
        },
      );
      yield eff.put(actions.succeeded({
        resources: [client],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* fetchClientEntity({
    list,
    clientId,
    requestKey,
    resourceType,
  }) {
    const actions = createActionCreators('read', {
      list,
      resourceType,
      requestKey: requestKey || `fetch${capitalizeFirstLetter(resourceType)}`,
    });

    yield eff.put(actions.pending());

    try {
      const currentUserId = yield eff.select(
        getProfile('id'),
      );

      const userId = clientId || currentUserId;
      const resources = yield eff.call(
        api.getPersonalEntity,
        userId
          ? {
            type: getEntityType(resourceType),
            userId,
          } : {
            type: getEntityType(resourceType),
          },
      );

      yield eff.put(actions.succeeded({
        resources,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* addEntity({
    list,
    payload,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      list,
      requestKey: 'createClientEntity',
      resourceType,
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const response = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: payload,
        },
      );

      if (payload.invite) {
        yield eff.put(
          requestInviteClient({
            userId: response.id,
            payload: {
              email: payload.email,
            },
          }),
        );
      }

      yield eff.put(actions.succeeded({
        resources: [{
          ...payload,
          ...response,
        }],
      }));

      const notifyKeys = {
        clients: 'client.added',
        clientGroups: 'client_groups.added',
      };

      if (notifyKeys[resourceType]) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: notifyKeys[resourceType],
            },
          },
        );
      }

      if (resourceType === 'clients') {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: 'client_added',
          attrs: {
            email: payload.email,
            full_name: `${payload.first_name} ${payload.last_name}`,
          },
        });
      }

      if (resourceType === 'clientGroups') {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: 'client_group_created',
          attrs: {
            id: response.id,
            name: `${payload.name}`,
          },
        });
      }

      callbacks?.setSubmitting?.(false);
      if (callbacks?.onSuccess && resourceType !== 'workoutTemplates' && resourceType !== 'programTemplates') {
        callbacks.onSuccess(response);
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.setSubmitting?.(false);
      if (e?.response?.data) {
        callbacks.setErrors?.(e.response.data.error);
      }

      yield eff.call(throwError, e);
    }
  }

  function* deleteEntity({
    id,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      requestKey: 'deleteClientEntity',
    });

    yield eff.put(actions.pending({
      resources: [id],
    }));

    try {
      const response = yield eff.call(
        api.deleteEntity,
        {
          id,
          type: getEntityType(resourceType),
        },
      );

      yield eff.put(
        selection.deselectResources(
          resourceType,
          [id],
        ),
      );

      yield eff.put(actions.succeeded({
        resources: resourceType === 'clients'
          ? [
            {
              ...response,
              id: response.client_id,
            },
          ]
          : [response],
      }));

      if (callbacks?.onSuccess) callbacks.onSuccess(response);
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.deleted',
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      if (callbacks?.onError) callbacks.onError(e);
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            key: 'item.not_deleted',
          },
        },
      );

      yield eff.call(throwError, e);
    }
  }

  function* inviteClient({
    userId,
    payload,
    callbacks,
  }) {
    try {
      yield eff.call(
        api.inviteUser,
        {
          userId,
          body: {
            email: payload.email,
          },
        },
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'user.invitation.sent',
          },
        },
      );

      const client = yield eff.select(
        getResourceById(
          'clients',
          userId,
        ),
      );

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'client_invited',
        attrs: {
          email: payload.email,
          full_name: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
        },
      });

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess();
    } catch (e) {
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);

      yield eff.call(throwError, e);

      if (payload.email) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'error',
              key: 'email.exists',
            },
          },
        );
      }
    }
  }

  function* deleteItems({
    entityType,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      requestKey: 'deleteClientItems',
    });
    yield eff.put(actions.pending());

    function* handleDelete(id) {
      try {
        yield eff.call(
          api.deleteEntity,
          {
            type: entityType || getEntityType(resourceType),
            id,
          },
        );

        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'item.deleted',
            },
          },
        );
        return id;
      } catch (e) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'error',
              key: 'item.not_deleted',
            },
          },
        );
        yield eff.call(throwError, e);
        return null;
      }
    }

    try {
      const selectedIds = yield eff.select(
        getSelectedIds(resourceType),
      );

      const results = yield eff.all(
        selectedIds.map(id => eff.call(handleDelete, id)),
      );

      yield eff.put(actions.succeeded({
        resources: results.filter(id => id),
      }));

      yield eff.put(
        selection.deselectResources(
          resourceType,
          selectedIds,
        ),
      );

      if (callbacks?.onSuccess) callbacks.onSuccess();
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      if (callbacks?.onError) callbacks.onError(e);
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);

      yield eff.call(throwError, e);
    }
  }

  // TODO merge with fetchClientEntity
  function* getClientPersonalEntity({
    qs,
    clientId,
    requestKey,
    entityType,
    resourceType,
  }) {
    const actions = createActionCreators('read', {
      list: `client_${clientId}`,
      requestKey: requestKey || `fetch${capitalizeFirstLetter(resourceType)}`,
      resourceType,
      mergeListIds: false,
    });

    yield eff.put(actions.pending());

    try {
      const currentUserId = yield eff.select(
        getProfile('id'),
      );
      const personalEntity = yield eff.call(
        api.getPersonalEntity,
        {
          qs,
          type: entityType || getEntityType(resourceType),
          userId: clientId || currentUserId,
        },
      );
      yield eff.put(actions.succeeded({
        resources: personalEntity,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* deletePersonalEntity({
    id,
    clientId,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      resources: [id],
      requestKey: 'deletePersonalEntity',
    });

    const exercisesActions = createActionCreators('read', {
      resourceType: 'personalExercises',
      requestKey: 'getPersonalExercise',
    });


    yield eff.put(actions.pending());
    yield eff.put(exercisesActions.pending());

    try {
      const deleteResponse = yield eff.call(
        api.deleteEntity,
        {
          id,
          type: getEntityType(resourceType),
        },
      );

      const personalExercises = yield eff.call(
        api.getPersonalEntity,
        {
          userId: clientId,
          type: 'personal_exercises',
        },
      );

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess();
      yield eff.put(actions.succeeded({
        resources: [deleteResponse],
      }));

      yield eff.put(exercisesActions.succeeded({
        resources: personalExercises,
        list: 'all',
        mergeListIds: false,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      yield eff.put(exercisesActions.failed({
        resources: [],
      }));


      yield eff.call(throwError, e);
    }
  }

  function* assignClient({
    payload,
    resourceType,
    listName,
  }) {
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'assignClient',
    });

    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: payload,
        },
      );

      // TODO FIX
      if (response.is_single_exercise && response.exercise_id) {
        response.id = response.exercise_id;
      }

      console.log('response', response);

      yield eff.put(actions.succeeded({
        resources: [response],
        includedResources: {
          assignees: [{
            id: response.id,
            person_id: payload.person_id,
          }],
          [resourceType]: [response],
        },
        customLists: {
          assignees: listName,
          [resourceType]: `client_${payload.person_id}`,
        },
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client.assigned',
            params: {
              type: ({
                personalWorkouts: 'WorkoutTemplate',
                personalPrograms: 'ProgramTemplate',
                personalExercises: 'Exercise',
              }[resourceType]),
            },
          },
        },
      );

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'library_item_assigned',
        attrs: {
          id: response.id,
          name: response.name,
          type: getEntityType(resourceType),
          personId: payload.person_id,
        },
      });
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* unAssignClient({
    id,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      resources: [id],
      requestKey: 'unAssignClient',
    });

    try {
      yield eff.put(actions.pending());

      const response = yield eff.call(
        api.deleteEntity,
        {
          id,
          type: getEntityType(resourceType),
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client.unassigned',
            params: {
              type: ({
                personalWorkouts: 'WorkoutTemplate',
                personalPrograms: 'ProgramTemplate',
                personalExercises: 'Exercise',
              }[resourceType]),
            },
          },
        },
      );
      if (callbacks?.onSuccess) {
        callbacks.onSuccess(response);
      }

      const assigned = yield eff.select(
        getResourceById(
          'assignees',
          id,
        ),
      );

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'library_item_unassigned',
        attrs: {
          id: response.id,
          name: response.name,
          type: getEntityType(resourceType),
          personId: assigned?.person_id,
        },
      });

      yield eff.put(deleteResources({
        resourceType: 'assignees',
        resources: [id],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.call(throwError, e);
    }
  }

  function* createPersonalEntity({
    payload,
    clientId,
    resourceType,
    callbacks,
  }) {
    const getTemplate = () => {
      switch (resourceType) {
        case 'personalPrograms':
          return 'programTemplates';
        case 'personalWorkouts':
          return 'workoutTemplates';
        default:
          return 'exercises';
      }
    };

    const getTemplateIdKey = () => {
      switch (resourceType) {
        case 'personalPrograms':
          return 'program_template_id';
        case 'personalWorkouts':
          return 'workout_template_id';
        default:
          return 'exercise_id';
      }
    };

    const actions = createActionCreators('read', {
      resourceType,
      requestKey: 'createPersonalEntity',
    });

    yield eff.put(actions.pending());

    try {
      const entityTemplate = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(getTemplate()),
          body: {
            ...payload,
          },
        },
      );
      const personalEntity = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: {
            person_id: clientId,
            [getTemplateIdKey()]: entityTemplate.id,
          },
        },
      );

      yield eff.call(
        api.deleteEntity,
        {
          id: entityTemplate.id,
          type: getEntityType(getTemplate()),
        },
      );

      yield eff.put(actions.succeeded({
        resources: [personalEntity],
        includedResources: {
          [getTemplate()]: [entityTemplate],
        },
        list: `client_${clientId}`,
      }));

      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }

      if (callbacks?.onSuccess) {
        callbacks.onSuccess(personalEntity);
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.call(throwError, e);
    }
  }

  function* updateClientInfo({
    payload,
    id,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'clients',
      requestKey: 'updateClientInfo',
    });

    yield eff.put(actions.pending());

    try {
      const result = yield eff.call(
        api.updateEntity,
        {
          id,
          type: 'user_profiles',
          body: payload,
        },
      );
      const profile = yield eff.select(
        getProfile('user_profile'),
      );

      if (profile.id === id) {
        yield eff.put(setProfileState({
          user_profile: {
            ...profile,
            ...payload,
          },
        }));
      }

      yield eff.put(actions.succeeded({
        resources: [{
          id: result.user_id,
          user_profile: payload,
        }],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: profile.id === id
              ? 'user.profile.updated'
              : 'client.updated',
          },
        },
      );

      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (e.response.status === 403) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'error',
              key: 'error.permission_denied_action',
            },
          },
        );
      }
      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }
      yield eff.call(throwError, e);
    }
  }

  function* getNotifications({ userId }) {
    const actions = createActionCreators('read', {
      resourceType: 'notifications',
      requestKey: 'fetchNotifications',
    });

    const readStatus = yield eff.select(
      getStatus,
      'notifications.requests.fetchNotifications.status',
    );

    try {
      if (readStatus.idle) {
        yield eff.put(actions.pending());
      }

      const currentUserId = yield eff.select(
        getProfile('id'),
      );

      if (currentUserId === userId) {
        const resources = yield eff.call(
          api.getNotifications,
        );

        yield eff.put(actions.succeeded({
          resources,
          list: String(userId),
        }));
      } else {
        const actionsWorkout = createActionCreators('read', {
          resourceType: 'workoutEvents',
          requestKey: 'fetchWorkoutEvents',
        });

        yield eff.put(actions.pending());
        yield eff.put(actionsWorkout.pending());

        const requestParams = {
          userId,
          qs: {
            pro_id: currentUserId,
          },
        };

        const resources = yield eff.call(
          api.getClientNotifications,
          requestParams,
        );

        const filtered = resources.filter(item => item.trackable_type === 'WorkoutEvent');

        const workoutEvents = yield eff.all(
          filtered.map(item => eff.call(
            api.getWorkoutEvent,
            {
              id: item.trackable_id,
            },
          )),
        );

        yield eff.put(actions.succeeded({
          resources,
          list: String(userId),
        }));

        yield eff.put(actionsWorkout.succeeded({
          resources: workoutEvents,
          list: String(userId),
        }));
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* archiveClients({ clients, callbacks }) {
    const actions = createActionCreators('delete', {
      resourceType: 'clients',
      requestKey: 'archiveClients',
    });

    try {
      const results = yield eff.all(
        clients.map(
          client => eff.call(
            api.archiveClient,
            {
              clientId: client.id,
            },
          ),
        ),
      );

      yield eff.put(
        selection.clearSelectedResources('clients'),
      );

      yield eff.put(actions.succeeded({
        resources: clients.map(c => c.id),
      }));

      yield eff.put(
        addResources({
          resourceType: 'archivedClients',
          resources: clients,
        }),
      );

      if (callbacks?.onSuccess) {
        callbacks.onSuccess(results);
      }

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            body: `Client${clients.length > 1 ? 's have' : ' has'} been archived.`,
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.call(throwError, e);
    }
  }

  function* restoreClients({ clients, callbacks }) {
    const actions = createActionCreators('delete', {
      resourceType: 'archivedClients',
      requestKey: 'restoreClients',
    });

    try {
      const results = yield eff.all(
        clients.map(
          client => eff.call(
            api.restoreClient,
            {
              clientId: client.id,
            },
          ),
        ),
      );

      yield eff.put(
        selection.clearSelectedResources('archivedClients'),
      );
      yield eff.put(actions.succeeded({
        resources: clients.map(c => c.id),
      }));

      yield eff.put(
        addResources({
          resourceType: 'clients',
          resources: clients,
        }),
      );

      if (callbacks?.onSuccess) {
        callbacks.onSuccess(results);
      }

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            body: `Client${clients.length > 1 ? 's have' : ' has'} been restored.`,
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.call(throwError, e);
    }
  }

  function* archiveTemplate({
    id,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      requestKey: 'archiveTemplate',
    });

    yield eff.put(actions.pending());

    try {
      const result = yield eff.call(
        api.archiveClientEntity,
        {
          id,
          type: getEntityType(resourceType),
        },
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.archived',
            params: {
              type: ({
                personalWorkouts: 'Workout',
                personalPrograms: 'Program',
              }[resourceType]),
            },
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [result],
      }));
    } catch (e) {
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* restoreTemplate({
    id,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      requestKey: 'archiveTemplate',
    });

    yield eff.put(actions.pending());

    try {
      const result = yield eff.call(
        api.restoreClientEntity,
        {
          id,
          type: getEntityType(resourceType),
        },
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.restored',
            params: {
              type: ({
                personalWorkouts: 'Workout',
                personalPrograms: 'Program',
              }[resourceType]),
            },
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [result],
      }));
    } catch (e) {
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery<any>(actionTypes.REQUEST_FETCH_CLIENT, fetchClient);
  yield eff.takeEvery<any>(actionTypes.REQUEST_FETCH_CLIENTS, fetchClients);
  yield eff.takeEvery<any>(actionTypes.REQUEST_FETCH_CLIENT_ENTITY, fetchClientEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_NOTIFICATIONS, getNotifications);
  yield eff.takeEvery<any>(actionTypes.REQUEST_CLIENT_PERSONAL_ENTITY, getClientPersonalEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_ADD_CLIENT_ENTITY, addEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_DELETE_CLIENT_ENTITY, deleteEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_DELETE_CLIENT_ITEMS, deleteItems);
  yield eff.takeEvery<any>(actionTypes.REQUEST_DELETE_PERSONAL_ENTITY, deletePersonalEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_INVITE_CLIENT, inviteClient);
  yield eff.takeEvery<any>(actionTypes.REQUEST_CREATE_PERSONAL_ENTITY, createPersonalEntity);
  yield eff.takeEvery<any>(actionTypes.REQUEST_UPDATE_CLIENT_INFO, updateClientInfo);
  yield eff.takeEvery<any>(actionTypes.REQUEST_ARCHIVE_CLIENTS, archiveClients);
  yield eff.takeEvery<any>(actionTypes.REQUEST_RESTORE_CLIENTS, restoreClients);
  yield eff.takeEvery<any>(actionTypes.REQUEST_ARCHIVE_TEMPLATE, archiveTemplate);
  yield eff.takeEvery<any>(actionTypes.REQUEST_RESTORE_TEMPLATE, restoreTemplate);

  yield eff.takeEvery<any>(actionTypes.REQUEST_ASSIGN_CLIENT, assignClient);
  yield eff.takeEvery<any>(actionTypes.REQUEST_UNASSIGN_CLIENT, unAssignClient);
}
